import React, {PureComponent} from 'react';
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import withStyles from '@mui/styles/withStyles';
import Fab from "@mui/material/Fab";
import AddIcon from '@mui/icons-material/Add';
import {PageDataContext} from "../../../core/PageDataContext";
import Grid from "@mui/material/Grid";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import Button from "@mui/material/Button";
import Zoom from "@mui/material/Zoom";
import {UserLocalStorage} from "../../../core/storage/UserLocalStorage";
import Skeleton from '@mui/material/Skeleton';
import LinearProgress from "@mui/material/LinearProgress";
import ExerciseNewOrEditFormPopUp from "../partials/ExerciseNewOrEditFormPopUp";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import {ColorPicker} from "../../../core/colors/ColorPicker";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {withRouter} from "../../../hooks/withRouter";

const useStyles = theme => ({
	speedDialWrapper: {
		margin: 0,
		top: 'auto',
		right: 35,
		bottom: 80,
		left: 'auto',
		position: 'fixed',
	},
	largeIcon: {
		width: 200,
		height: 200,
	},
	media: {
		height: 600,
	},
});

class ExercisesOverview extends PureComponent {
	static displayName = ExercisesOverview.name;

	/**
	 * @param props No params needed.
	 */
	constructor(props) {
		super(props);
		this.state = {
			exercises: UserLocalStorage.get(UrlBuilder.exercise.exercisesApi()) ?? null,
			showAddExercisePopUp: false,
			exerciseInEditableMode: null,
			isLoading: true,
			dropDownMenuAnchorEl: null,
			searchText: '',
			selectedCategory: null,
		};

		this.fetchExercises = this.fetchExercises.bind(this);
		this.refresh = this.refresh.bind(this);
		this.toggleOpenAddExercisePopup = this.toggleOpenAddExercisePopup.bind(this);
		this.toggleEditableExercise = this.toggleEditableExercise.bind(this);
		this.toggleCloseAllPopups = this.toggleCloseAllPopups.bind(this);
		this.renderExercises = this.renderExercises.bind(this);
	}

	componentDidMount() {
		this.context.setPageData({
			title: "My Exercises",
		});
		this.fetchExercises();
	}

	openDropdownMenu = (event) => {
		this.setState({
			dropDownMenuAnchorEl: event.currentTarget
		});
	};

	closeDropdownMenu = () => {
		this.setState({
			dropDownMenuAnchorEl: null
		});
	};
	
	refresh() {
		this.fetchExercises();
	}

	async fetchExercises() {
		this.setState({
			isLoading: true
		});

		const exercisesResponse = await fetch(UrlBuilder.exercise.exercisesApi());
		const exercises = await exercisesResponse.json();

		this.setState({
			isLoading: false,
			exercises: exercises, 
		});

		UserLocalStorage.set(UrlBuilder.exercise.exercisesApi(), exercises ?? []);
	}

	deleteExercise = async (event, exercise) => {
		event.preventDefault(); // Prevents it from reloading the page and adding the params to the URL.

		await fetch(UrlBuilder.exercise.exercisesApi(exercise.id), {
			method: 'DELETE',
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			},
		})
			.then(() => this.toggleCloseAllPopups(true));
	}

	toggleOpenAddExercisePopup() {
		this.setState({
			showAddExercisePopUp: !this.state.showAddExercisePopUp
		});
	}

	toggleEditableExercise(exercise) {
		this.setState({
			exerciseInEditableMode: exercise,
			dropDownMenuAnchorEl: null
		});
	}

	toggleCloseAllPopups(shouldRefresh = true) {
		this.setState({
			showAddExercisePopUp: false,
			exerciseInEditableMode: null,
			dropDownMenuAnchorEl: null
		});
		if (shouldRefresh) {
			this.refresh();
		}
	}

	getAllCategories = () => {
		if (!this.state.exercises) return [];
		const cats = new Set();
		this.state.exercises.forEach(e => {
			cats.add(e.category ? e.category.name : 'Uncategorized');
		});
		return [...cats].sort((a, b) => {
			if (a === 'Uncategorized') return 1;
			if (b === 'Uncategorized') return -1;
			return a.localeCompare(b);
		});
	}

	getFilteredExercises = () => {
		let exercises = this.state.exercises;
		const { searchText, selectedCategory } = this.state;

		if (selectedCategory) {
			exercises = exercises.filter(e => {
				const cat = e.category ? e.category.name : 'Uncategorized';
				return cat === selectedCategory;
			});
		}

		const query = searchText.toLowerCase().trim();
		if (!query) return exercises;
		return exercises.filter(e =>
			e.name.toLowerCase().includes(query) ||
			(e.category && e.category.name.toLowerCase().includes(query)) ||
			e.type.name.toLowerCase().includes(query)
		);
	}

	getGroupedExercises = (exercises) => {
		const groups = {};
		exercises.forEach(exercise => {
			const cat = exercise.category ? exercise.category.name : 'Uncategorized';
			if (!groups[cat]) groups[cat] = [];
			groups[cat].push(exercise);
		});
		return Object.entries(groups).sort(([a], [b]) => {
			if (a === 'Uncategorized') return 1;
			if (b === 'Uncategorized') return -1;
			return a.localeCompare(b);
		});
	}

	renderExercises() {
		const filtered = this.getFilteredExercises();
		const grouped = this.getGroupedExercises(filtered);
		const allCategories = this.getAllCategories();

		return <>
			<Paper
				variant="outlined"
				sx={{
					display: 'flex',
					alignItems: 'center',
					borderRadius: 2,
					px: 1,
					mb: 1.5,
				}}
			>
				<IconButton size="small" sx={{ mr: 0.5 }}>
					<SearchIcon />
				</IconButton>
				<InputBase
					sx={{ flex: 1, fontSize: 14 }}
					placeholder="Search exercises..."
					onChange={(e) => this.setState({ searchText: e.target.value })}
					value={this.state.searchText}
				/>
				{this.state.searchText && (
					<IconButton size="small" onClick={() => this.setState({ searchText: '' })}>
						<CloseIcon fontSize="small" />
					</IconButton>
				)}
			</Paper>

			{allCategories.length > 1 && (
				<Box sx={{
					display: 'flex',
					gap: 0.75,
					overflowX: 'auto',
					pb: 1,
					mb: 0.5,
					'&::-webkit-scrollbar': { display: 'none' },
					scrollbarWidth: 'none',
				}}>
					<Chip
						label="All"
						size="small"
						variant={!this.state.selectedCategory ? 'filled' : 'outlined'}
						color={!this.state.selectedCategory ? 'secondary' : 'default'}
						onClick={() => this.setState({ selectedCategory: null })}
						sx={{ fontWeight: 500, fontSize: 12 }}
					/>
					{allCategories.map(cat => (
						<Chip
							key={cat}
							label={cat}
							size="small"
							variant={this.state.selectedCategory === cat ? 'filled' : 'outlined'}
							color={this.state.selectedCategory === cat ? 'secondary' : 'default'}
							onClick={() => this.setState({
								selectedCategory: this.state.selectedCategory === cat ? null : cat
							})}
							sx={{ fontWeight: 500, fontSize: 12 }}
						/>
					))}
				</Box>
			)}

			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
				<Typography variant="caption" sx={{ color: 'text.secondary' }}>
					{filtered.length} exercise{filtered.length !== 1 ? 's' : ''}
					{this.state.searchText ? ` matching "${this.state.searchText}"` : ''}
					{this.state.selectedCategory ? ` in ${this.state.selectedCategory}` : ' in your library'}
				</Typography>
			</Box>

			{filtered.length === 0 ? (
				<Box sx={{ textAlign: 'center', py: 5 }}>
					<Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
						{this.state.searchText
							? `No exercises matching "${this.state.searchText}"`
							: `No exercises in ${this.state.selectedCategory}`}
					</Typography>
					{this.state.searchText && (
						<Button
							size="small"
							startIcon={<AddIcon />}
							onClick={this.toggleOpenAddExercisePopup}
							sx={{ textTransform: 'none' }}
						>
							Create "{this.state.searchText}"
						</Button>
					)}
				</Box>
			) : (
				grouped.map(([category, exercises]) => (
					<Box key={category} sx={{ mb: 1 }}>
						<Typography variant="overline" sx={{
							color: 'text.secondary',
							fontSize: 11,
							letterSpacing: 1,
							display: 'block',
							position: 'sticky',
							top: 48,
							bgcolor: 'background.default',
							zIndex: 1,
							pt: 1,
							pb: 0.25,
						}}>
							{category}
						</Typography>
						<List disablePadding>
							{exercises.map(exercise => (
								<ListItem
									key={exercise.id}
									button
									onClick={() => this.props.navigate(UrlBuilder.exercise.exerciseDetailPage(exercise.id))}
									sx={{
										borderRadius: 2,
										px: 1.5,
										mb: 0.25,
										'&:hover': { bgcolor: 'action.hover' },
									}}
								>
									<ListItemAvatar sx={{ minWidth: 40 }}>
										<Avatar
											sx={{
												width: 32,
												height: 32,
												fontSize: 14,
												fontWeight: 600,
												bgcolor: exercise.color ?? ColorPicker.pick(exercise.name),
											}}
										>
											{exercise.name.charAt(0).toUpperCase()}
										</Avatar>
									</ListItemAvatar>
									<ListItemText
										primary={
											<Typography variant="body2" sx={{ fontWeight: 500 }}>
												{exercise.name}
											</Typography>
										}
										secondary={
											<Typography variant="caption" sx={{ color: 'text.secondary' }}>
												{exercise.type.name}
											</Typography>
										}
									/>
									<ChevronRightIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
								</ListItem>
							))}
						</List>
					</Box>
				))
			)}

			<Box paddingTop={3}/>
		</>
	}

	renderNoExercises = () => {
		return (
			<Box sx={{
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 3,
				bgcolor: 'background.paper',
				p: { xs: 3, sm: 4 },
				textAlign: 'center',
				maxWidth: 480,
				mx: 'auto',
				mt: 4,
			}}>
				<Box
					onClick={this.toggleOpenAddExercisePopup}
					sx={{
						width: 56, height: 56, borderRadius: '50%',
						bgcolor: (theme) => theme.palette.mode === 'dark'
							? 'rgba(245,0,87,0.12)' : 'rgba(245,0,87,0.06)',
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						mx: 'auto', mb: 2,
						cursor: 'pointer',
						transition: 'background-color 0.2s',
						'&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark'
							? 'rgba(245,0,87,0.2)' : 'rgba(245,0,87,0.1)' },
					}}
				>
					<AddToPhotosIcon sx={{ fontSize: 28, color: 'secondary.main' }} />
				</Box>

				<Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
					No exercises yet
				</Typography>
				<Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
					Exercises are the building blocks of your routines. Create custom ones with muscle breakdowns, or add them later from a routine.
				</Typography>

				<Button
					variant="contained"
					color="secondary"
					size="large"
					startIcon={<AddToPhotosIcon />}
					onClick={this.toggleOpenAddExercisePopup}
					sx={{ px: 3, textTransform: 'none', fontWeight: 600 }}
				>
					Create an Exercise
				</Button>
			</Box>
		);
	}
	
	renderPage = () => {
		if (this.state.exercises && this.state.exercises.length > 0) {
			return this.renderExercises();
		}
		return this.renderNoExercises();
	}

	renderLoadingSkeletons = () => {
		const skeletonItems = [4, 3, 2];
		return <>
			<Skeleton variant="rounded" height={40} sx={{ borderRadius: 2, mb: 1.5 }} />
			<Skeleton variant="text" width={100} sx={{ mb: 0.5 }} />
			{skeletonItems.map((count, groupIdx) => (
				<Box key={groupIdx} sx={{ mb: 1 }}>
					<Skeleton variant="text" width={80} sx={{ fontSize: 11, mb: 0.5 }} />
					{Array.from({ length: count }).map((_, i) => (
						<Box key={i} sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.75 }}>
							<Skeleton variant="circular" width={32} height={32} sx={{ mr: 1 }} />
							<Box sx={{ flex: 1 }}>
								<Skeleton variant="text" width={`${50 + Math.random() * 30}%`} sx={{ fontSize: 14 }} />
								<Skeleton variant="text" width={60} sx={{ fontSize: 12 }} />
							</Box>
						</Box>
					))}
				</Box>
			))}
		</>;
	}
	
	hasNeverBeenFetched = () => {
		return this.state.exercises === null;
	}

	render() {
		const {classes} = this.props;
		
		return (
			<>
				<Fade in={this.state.isLoading}>
					<LinearProgress />
				</Fade>

				<Box paddingTop={1} />

				<ExerciseNewOrEditFormPopUp
					isOpen={this.state.showAddExercisePopUp || this.state.exerciseInEditableMode}
					exercise={this.state.exerciseInEditableMode}
					deleteExerciseFunc={this.deleteExercise}
					closeSelfFunc={this.toggleCloseAllPopups}
				/>

				{
					this.hasNeverBeenFetched() ?
						this.renderLoadingSkeletons()
						: this.renderPage()
				}

				<Zoom in={true} unmountOnExit>
					<Fab color="primary" className={classes.speedDialWrapper} aria-label="add" onClick={this.toggleOpenAddExercisePopup}>
						<AddIcon />
					</Fab>
				</Zoom>
			</>
		);
	}
}

ExercisesOverview.contextType = PageDataContext;

export default withRouter(withStyles(useStyles)(ExercisesOverview));